/**
 * Single source of truth for onboarding / publish readiness.
 *
 * Pure and deterministic: given the store settings object (and optional widget
 * status), it returns the setup steps with completion state. Both the Dashboard
 * checklist and the Publish gate consume this so they never disagree.
 */
import { ROUTE } from './navigation';
import { isQuoteDistributionUiEnabled } from './featureFlags';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Providers that need no host/user/pass (managed relay). */
const DEFAULT_SMTP_PROVIDERS = ['FreshClick Server (default)', 'Quote2Cart Default'];

/** Placeholder / demo hosts that must not count as configured storefront URLs. */
const PLACEHOLDER_HOSTS =
  /example\.company\.site|example\.mybigcommerce\.com|yourstore\.com/i;

function isNonEmpty(value) {
  return typeof value === 'string' ? value.trim().length > 0 : value != null && value !== '';
}

function isValidEmail(value) {
  return typeof value === 'string' && EMAIL_RE.test(value.trim());
}

function isUsableHttpUrl(value) {
  const u = typeof value === 'string' ? value.trim() : '';
  if (!u) return false;
  if (!/^https?:\/\//i.test(u)) return false;
  if (PLACEHOLDER_HOSTS.test(u)) return false;
  return true;
}

function isTruthyFlag(value) {
  return value === true || value === 1 || value === '1' || value === 'true';
}

/**
 * GET /settings returns `{ settings: StoreSettings }`. Section endpoints and
 * some callers pass the flat object. Accept either shape.
 */
export function normalizeSettings(payload) {
  if (!payload || typeof payload !== 'object') return {};
  if (payload.settings && typeof payload.settings === 'object') {
    return payload.settings;
  }
  return payload;
}

function usesDefaultSmtp(settings) {
  const provider = settings.smtpProvider;
  if (!isNonEmpty(provider)) return true;
  return DEFAULT_SMTP_PROVIDERS.indexOf(String(provider).trim()) !== -1;
}

function isStoreProfileComplete(settings) {
  return (
    isNonEmpty(settings.companyName) &&
    isValidEmail(settings.supportEmail) &&
    isNonEmpty(settings.phone) &&
    isUsableHttpUrl(settings.storeWebsite)
  );
}

function isBasicSetupComplete(settings) {
  return isTruthyFlag(settings.enableQuotes);
}

function isCustomerInterfaceComplete(settings) {
  return isNonEmpty(settings.addToQuoteButtonText) && isNonEmpty(settings.cartPageButtonText);
}

function isEmailComplete(settings) {
  if (usesDefaultSmtp(settings)) return true;
  return (
    isNonEmpty(settings.smtpHost) &&
    isNonEmpty(settings.smtpUser) &&
    (isNonEmpty(settings.smtpPass) || Boolean(settings.smtpPassConfigured))
  );
}

/**
 * @param {object|null} settings   Store settings or `{ settings: StoreSettings }`.
 * @param {object|null} widgetStatus  Result of api.getWidgetStatus().status.
 * @returns {{
 *   steps: Array<{ id, label, labelKey, route, required, complete, reason, reasonKey }>,
 *   requiredComplete: boolean,
 *   missingRequired: Array<{ id, label, labelKey, route, reason, reasonKey }>,
 *   completedCount: number,
 *   totalCount: number,
 *   live: boolean
 * }}
 */
export function computeSetupSteps(settings, widgetStatus) {
  const s = normalizeSettings(settings);
  const live = Boolean(widgetStatus && widgetStatus.live);

  const steps = [
    {
      id: 'store-profile',
      label: 'Store Profile',
      labelKey: 'settings.setupSteps.storeProfileLabel',
      route: ROUTE.SETTINGS_STORE_PROFILE,
      required: true,
      complete: isStoreProfileComplete(s),
      reason: 'Add company name, support email, phone, and store website.',
      reasonKey: 'settings.setupSteps.storeProfileReason',
    },
    {
      id: 'basic-setup',
      label: 'Basic Setup',
      labelKey: 'settings.setupSteps.basicSetupLabel',
      route: ROUTE.SETTINGS_BASIC_SETUP,
      required: true,
      complete: isBasicSetupComplete(s),
      reason: 'Turn on "Enable Quotes".',
      reasonKey: 'settings.setupSteps.basicSetupReason',
    },
    {
      id: 'customer-interface',
      label: 'Customer Interface',
      labelKey: 'settings.setupSteps.customerInterfaceLabel',
      route: ROUTE.SETTINGS_CUSTOMER_INTERFACE,
      required: true,
      complete: isCustomerInterfaceComplete(s),
      reason: 'Set the product and cart button labels.',
      reasonKey: 'settings.setupSteps.customerInterfaceReason',
    },
    {
      id: 'email',
      label: 'Email & SMTP',
      labelKey: 'settings.setupSteps.emailLabel',
      route: ROUTE.SETTINGS_EMAIL,
      required: true,
      complete: isEmailComplete(s),
      reason: 'Use the default sender or complete SMTP host, user, and password.',
      reasonKey: 'settings.setupSteps.emailReason',
    },
    {
      id: 'distribution',
      label: 'Quote Distribution',
      labelKey: 'settings.setupSteps.distributionLabel',
      route: ROUTE.SETTINGS_DISTRIBUTION,
      required: false,
      complete: isNonEmpty(s.distributionMode),
      reason: 'Choose how incoming quotes are assigned.',
      reasonKey: 'settings.setupSteps.distributionReason',
      hidden: !isQuoteDistributionUiEnabled(),
    },
  ].filter((step) => !step.hidden);

  const missingRequired = steps
    .filter((step) => step.required && !step.complete)
    .map(({ id, label, labelKey, route, reason, reasonKey }) => ({
      id,
      label,
      labelKey,
      route,
      reason,
      reasonKey,
    }));

  const completedCount = steps.filter((step) => step.complete).length;

  return {
    steps,
    requiredComplete: missingRequired.length === 0,
    missingRequired,
    completedCount,
    totalCount: steps.length,
    live,
  };
}
